import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from '@/App';
import { InvalidUrl } from '@/components/InvalidUrl';
import { ConfigProvider } from '@/config/ConfigProvider';
import { loadConfig } from '@/config/loadConfig';
import { useSignageStore } from '@/store/signageStore';
import '@/styles/index.css';

/**
 * Bootstrap. The config is loaded *before* the tree mounts, exactly as the legacy app did:
 * it means no component ever sees an undefined config, which removes a class of guard code
 * from every widget.
 *
 * StrictMode stays on deliberately. It double-invokes effects in dev, which is how the
 * MQTT connect (SYM-489) gets its idempotency checked on a laptop instead of on a wall.
 */
async function bootstrap(): Promise<void> {
  const result = await loadConfig();
  const root = document.getElementById('root');
  if (root === null) {
    console.error('[signage] #root is missing from index.html; nothing can be rendered');
    return;
  }

  if (result.ok) {
    // Hydrated here, synchronously, *before* the tree mounts — not from an effect in `App`.
    // React runs child effects before parent effects, so a widget's registration effect
    // (`useWidgetData`, SYM-491+) would fire before an `App`-level hydrate and would build its
    // topic key against an empty `baseTopic` — the very race the atomic hydrate exists to
    // close (docs/MIGRATION.md §4). This is also the earliest point the config exists, and it
    // runs exactly once regardless of StrictMode.
    useSignageStore.getState().hydrateFromConfig(result.config);
    const { mqttUrl, heartBeat, baseTopic, floorCode } = useSignageStore.getState();
    console.info('[signage] store hydrated', { mqttUrl, heartBeat, baseTopic, floorCode });

    // Same ordering argument, one element up: `getDateAndTime()` reads `<html lang>` to pick
    // the locale, and React runs child effects before parent effects — so a widget that
    // formats a date on mount would see the document default, not the config, if `lang` were
    // only set from `useDocumentTheme` in `App`. That hook still owns the attribute (and
    // restores it); this line just guarantees it is already right for the first render.
    document.documentElement.lang = result.config.locale;
  }

  createRoot(root).render(
    <StrictMode>
      {result.ok ? (
        <ConfigProvider config={result.config}>
          <App />
        </ConfigProvider>
      ) : (
        <InvalidUrl reason={result.reason} />
      )}
    </StrictMode>,
  );
}

void bootstrap();
